import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";

import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { useParams } from "react-router";
import { emitToasterMsg } from "../../../../../services/toasterUtility";

const FeuC21InstrumentsInvolved = forwardRef(({ FeuC21InstrumentsInvolved, refetchMonitoringData, dropdownData,vesselMstId }, ref) => {

    const RowActions = {
        Edit: 0,
        Add: 1,
    };

    const [activeAction, setactiveAction] = useState(-1);
    const [fuelData, setFuelData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [showOtherColumn, setShowOtherColumn] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingRowIndex, setDeletingRowIndex] = useState();

    useEffect(() => {
        if (Array.isArray(FeuC21InstrumentsInvolved)) {
            const transformedData = FeuC21InstrumentsInvolved?.map((item) => {
                return {
                    equipmentName: item.equipmentName || "",
                    other: item.other || "",
                    technicalDescription: item.technicalDescription || "",

                    elementsApplied: Array.isArray(item.elementsApplied)
                        ? item.elementsApplied
                        : typeof item.elementsApplied === 'string'
                            ? item.elementsApplied.split(',')?.map((v) => v.trim())
                            : [],
                };
            });

            setFuelData(transformedData);
        }
    }, [FeuC21InstrumentsInvolved]);

    const handleFuelActions = async (table, row, action) => {
        if (activeAction === -1) {
            if (action === RowActions.Edit) {
                table.setEditingRow(row);
                setactiveAction(action);
            } else if (action === RowActions.Add) {
                table.setCreatingRow(true);
                setactiveAction(action);
            }
            return;
        } else return;
    };

    const resetActiveRow = () => {
        setactiveAction(-1);
        setValidationErrors({});
    };


    const validateRequired = (value) => {
        if (typeof value === "string") {
            return !!value.length;
        } else if (typeof value === "number") {
            return value >= 0;
        }
        return false;
    };

    useImperativeHandle(ref, () => ({
        async submitAndCheck() {
            const { creatingRow, editingRow } = table.getState();
            if (!creatingRow && !editingRow) return true;
            const mode = creatingRow ? "add" : "edit";
            const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
            let isDirty = false;
            if (editingRow && editedValues) {
                isDirty = Object.keys(editedValues).some(
                    (key) => editedValues[key] !== editingRow.original[key]
                );
            }
            const validationRes = validateUser(editedValues, mode, {});
            const hasErrors = Object.values(validationRes).some((err) => err);

            if (hasErrors) {
                setValidationErrors(validationRes);
                emitToasterMsg(
                    "warning",
                    "Validation Error",
                    "Please correct the highlighted fields before proceeding."
                );
                return false;
            }

            // Autosave on valid & dirty
            if (creatingRow) {
                await handleCreateFuel({ values: editedValues, table }, true);
                return true;
            } else if (editingRow && isDirty) {
                await handleUpdateEmissions({ values: editedValues, table, row: editingRow }, true);
                return true;
            }

            return true;
        }
    }));

    const handleCreateFuel = async ({ values, table }, isAuto = false) => {
        const newValidationErrors = validateUser(values, "add", {});
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        const newRow = {
            FeuC21InstrumentsInvolved: 0,
            equipmentName: values.equipmentName,
            elementsApplied: values.elementsApplied || [],
            other: values.other,
            technicalDescription: values.technicalDescription,
        };
        const payload = fuelData?.map((item) => ({
            ...item,
            FeuC21InstrumentsInvolved: 0
        }));
        const finalPayload = [...payload, newRow];

        try {
            const FeuC21InstrumentsInvolved = await callApi({
                method: "POST",
                url: API_ROUTES.C21(vesselMstId),
                data: finalPayload,
            });
            if (FeuC21InstrumentsInvolved?.isSuccess) {
                emitToasterMsg("success", "Table C.2.1", isAuto ? "Emission source has been autosaved successfully" : "Emission source has been added successfully");
                // emitToasterMsg("success", "Table C21", "Emission source has been added successfully");
                table.setCreatingRow(null);
                setFuelData(finalPayload);
                setactiveAction(-1);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }

            }
            else {
                emitToasterMsg("error", "Table C.2.1", FeuC21InstrumentsInvolved?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.2.1", error.errorMessage);
        }
    };

    const handleUpdateEmissions = async ({ values, table, row }, isAuto = false) => {
        const updated = fuelData.map((item, index) => {
            if (index === row.index) {
                return {
                    ...item,
                    equipmentName: values.equipmentName,
                    elementsApplied: values.elementsApplied || [],
                    other: values.other,
                    technicalDescription: values.technicalDescription,
                };
            }
            return item;
        });

        try {
            const response = await callApi({
                method: "POST",
                url: API_ROUTES.C21(vesselMstId),
                data: updated,
            });
            if (response?.isSuccess) {
                emitToasterMsg("success", "Table C.2.1", isAuto ? "Emission source has been autosaved successfully" : "Emission source has been updated successfully");
                // emitToasterMsg("success", "Table C21", "Emission source has been updated successfully");
                table.setEditingRow(null);
                setactiveAction(-1);
                setFuelData(updated);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.2.1", response?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.2.1", error.errorMessage);
        }
    };

    const handleDeleteEmissions = async (rowIndex) => {
        const updated = fuelData.filter((_, i) => i !== rowIndex);
        try {
            const FeuC21InstrumentsInvolved = await callApi({
                method: "POST",
                url: API_ROUTES.C21(vesselMstId),
                data: updated,
            });
            if (FeuC21InstrumentsInvolved?.isSuccess) {
                emitToasterMsg("success", "Table C.2.1", "Emission source has been deleted successfully");
                setactiveAction(-1);
                setFuelData(updated);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.2.1", FeuC21InstrumentsInvolved?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.2.1", error.errorMessage);
        }
        setConfirmDelete(false);
    };

    const validateUser = (row) => {
        return {
            equipmentName: !validateRequired(row.equipmentName)
                ? "Field is required"
                : "",
            elementsApplied: !row.elementsApplied || row.elementsApplied.length === 0
                ? "Field is required"
                : "",
        };
    };

    // build the option list once dropdownData is present
    const fuelTypeOptions = useMemo(() => {
        return (dropdownData?.elementsApplieds ?? []).map((el) => ({
            code: String(el.code ?? ""),
            name: String(el.value ?? el.code ?? ""),
        }));
    }, [dropdownData]);

    const getFuelTypeOptions = useMemo(() => {
        return fuelTypeOptions.map((opt) => ({
            value: opt.code,
            label: opt.name,
        }));
    }, [fuelTypeOptions]);



    const columns = useMemo(() => {
        const baseColumns = [
            {
                accessorKey: "equipmentName",
                header: "Measurement equipment (name)",
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                muiEditTextFieldProps: ({ row }) => ({
                    required: true,
                    error: !!validationErrors?.equipmentName,
                    helperText: validationErrors?.equipmentName,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            equipmentName: undefined,
                        }),
                    inputProps: {
                        maxLength: 255,
                    },
                }),
            },
            {
                accessorKey: "elementsApplied",
                header: "Elements applied to (e.g. fuel consumer units, tanks, ...)",
                editVariant: "select",
                editSelectOptions: getFuelTypeOptions,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    select: true,
                    required: true,
                    value: cell.getValue() || [],
                    error: !!validationErrors?.elementsApplied,
                    helperText: validationErrors?.elementsApplied,
                    SelectProps: {
                        multiple: true,
                        renderValue: (selected) => {
                            if (!selected || selected.length === 0) return "Select fuel type(s)";
                            return (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((code) => {
                                        const label = fuelTypeOptions.find((opt) => opt.code === code)?.name || code;
                                        return (
                                            <Chip
                                                key={code}
                                                label={label}
                                                onDelete={(e) => {
                                                    e.stopPropagation();
                                                    const newValue = selected.filter((v) => v !== code);
                                                    row._valuesCache["elementsApplied"] = newValue;
                                                    if (code === "Other") {
                                                        row._valuesCache["other"] = "";
                                                    }
                                                    setShowOtherColumn(newValue.includes("Other"));
                                                    setValidationErrors((prev) => ({ ...prev, elementsApplied: undefined }));
                                                }}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            />
                                        );
                                    })}
                                </Box>
                            );
                        },
                    },
                    onChange: (event) => {
                        const value = event.target.value ?? [];
                        const normalized = Array.isArray(value) ? [...new Set(value.map((v) => String(v)))] : [String(value)];
                        row._valuesCache["elementsApplied"] = normalized;
                        if (!normalized.includes("Other")) {
                            row._valuesCache["other"] = "";
                        }
                        setShowOtherColumn(normalized.includes("Other"));
                        setValidationErrors((prev) => ({ ...prev, elementsApplied: undefined }));
                    },
                }),
                Cell: ({ cell }) => (cell.getValue() || []).map((code) => fuelTypeOptions.find((opt) => opt.code === code)?.name || code).join(", "),
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: "none",
                        borderRight: "1px solid lightgray",
                        verticalAlign: "middle",
                    },
                },
            },
            {
                accessorKey: "other",
                header: "Other",
                muiEditTextFieldProps: ({ row }) => ({
                    disabled: !showOtherColumn,
                    value: row._valuesCache?.other ?? "",
                    onChange: (e) => {
                        row._valuesCache["other"] = e.target.value;
                    },
                    placeholder: "Specify other fuel type",
                    inputProps: {
                        maxLength: 255,
                    },
                }),
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
            },

            {
                accessorKey: "technicalDescription",
                header: "Technical description (specification, age, calibration methods and intervals, maintenance intervals)",
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                muiEditTextFieldProps: {
                    inputProps: {
                        maxLength: 1000,
                    },
                },
            },
        ];

        return baseColumns;
    }, [fuelTypeOptions, validationErrors, showOtherColumn]);

    const handleDeleteBtnClick = (rowIndex) => {
        setConfirmDelete(true);
        setDeletingRowIndex(rowIndex);
    };

    const table = useMaterialReactTable({
        columns,
        data: fuelData || [],
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: true,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: ({ row, cell }) => {
            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                    color: "",
                },
            };
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                verticalAlign: "top",

            },
        }),
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        columnOrder: [
            "emissionSource",
            "nameOfEmissionsSource",
            "typeOfEmissionsSource",
            "emissionsSourceClass",
            "techinal",
            "nameOfEmissionsSource"

        ],

        getRowId: (row) => { },

        onCreatingRowSave: handleCreateFuel,
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleUpdateEmissions,
        renderToolbarInternalActions: ({ table }) => <></>,
        renderRowActions: ({ row, table }) => {
            return (
                <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Tooltip title="Edit">
                        <IconButton
                            style={{ padding: "0px" }}
                            onClick={() =>
                                handleFuelActions(
                                    table,
                                    row,
                                    RowActions.Edit
                                )
                            }
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            style={{ padding: "0px" }}
                            color="error"
                            onClick={() => handleDeleteBtnClick(row?.index)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <div className="mt-1">
                <Button
                    startIcon={<Icon icon={ICONS.ADD} />}
                    onClick={() => {
                        handleFuelActions(table, null, RowActions.Add);
                    }}
                    disabled={!!table.getState().creatingRow}
                >
                    Add Measurement Equipment
                </Button>
            </div>
        ),
    });

    return (
        <>
            <div className="fuel-registry-table mt-1">
                <MaterialReactTable table={table} />
            </div>
            <div className="confirmation-modal-container">
                <Modal
                    isOpen={confirmDelete}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    onCloseClickListener={() => {
                        setConfirmDelete(false);
                    }}
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                onClick={() => { handleDeleteEmissions(deletingRowIndex) }}
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Delete
                            </Button>
                            <Button
                                className="modal-cancel-btn"
                                onClick={() => {
                                    setConfirmDelete(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <div>Are you sure you want to delete this measuring equipment?</div>
                </Modal>
            </div>
        </>
    );
})

export default FeuC21InstrumentsInvolved;